import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { ClearCircle } from '@/components/Icons/ClearCircle';
import { Close } from '@/components/Icons/Close';
import { MailRead } from '@/components/Icons/MailRead';
import { Retry } from '@/components/Icons/Retry';
import { Search } from '@/components/Icons/Search';
import { Trash } from '@/components/Icons/Trash';
import { MailActionsMenu } from '@/pages/Mail/components/MailActionsMenu';
import { MailSearchTypeDropdown } from '@/pages/Mail/components/MailSearchTypeDropdown';
import { getMailFolderActions } from '@/pages/Mail/mailFolders';
import {
  BorderRadius,
  ButtonType,
  ButtonVariant,
  type MailSearchType,
  Size,
} from '@/types/components';
import type { MailFolder } from '@/types/mail';
import { tv } from '@/utils/tv';

const actionButton = tv({
  base: 'text-subhead-14 font-semibold flex h-9 cursor-pointer items-center gap-2 rounded-3xl px-4 transition-colors disabled:cursor-not-allowed',
  variants: {
    disabled: {
      true: 'bg-secondary/40 text-muted-foreground/40',
      false: 'bg-purple-soft text-primary hover:bg-purple-soft/70',
    },
  },
});

interface Props {
  folder: MailFolder;
  count: number;
  searchQuery: string;
  searchType: MailSearchType | null;
  isSelecting: boolean;
  selectedCount: number;
  areAllSelected: boolean;
  onSearchChange: (value: string) => void;
  onSearchTypeChange: (type: MailSearchType) => void;
  onSearchSubmit: () => void;
  onMark: () => void;
  onMarkAllRead: () => void;
  onMarkSelectedRead: () => void;
  onToggleAll: () => void;
  onDelete: () => void;
  onRestore: () => void;
  onDeletePermanently: () => void;
  onCancel: () => void;
}

export const MailToolbar: React.FC<Props> = ({
  folder,
  count,
  searchQuery,
  searchType,
  isSelecting,
  selectedCount,
  areAllSelected,
  onSearchChange,
  onSearchTypeChange,
  onSearchSubmit,
  onMark,
  onMarkAllRead,
  onMarkSelectedRead,
  onToggleAll,
  onDelete,
  onRestore,
  onDeletePermanently,
  onCancel,
}) => {
  const { t } = useTranslation();

  const {
    canSelect,
    canMarkAllAsRead,
    canMarkAsRead,
    canDelete,
    canRestore,
    canDeletePermanently,
  } = getMailFolderActions(folder);
  const title = `${folder.name} (${count})`;
  const hasSelection = selectedCount > 0;

  return (
    <div className="flex flex-col gap-3 pt-2">
      {folder.searchTypes.length > 0 && (
        <div className="px-2">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onSearchSubmit();
            }}
            className="flex items-center gap-4 rounded-[20px] border border-transparent bg-on-primary-surface p-1 contrast:border-border"
          >
            <MailSearchTypeDropdown
              types={folder.searchTypes}
              value={searchType}
              onChange={onSearchTypeChange}
            />
            <div className="flex flex-1 items-center gap-2">
              <Search className="size-4 shrink-0 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                placeholder={t('mail.search')}
                enterKeyHint="search"
                onChange={(event) => onSearchChange(event.target.value)}
                className="text-body-14 w-full bg-transparent text-foreground outline-none placeholder:text-muted-foreground"
              />
              {searchQuery && (
                <button
                  type="button"
                  aria-label={t('global.close')}
                  onClick={() => onSearchChange('')}
                  className="flex shrink-0 cursor-pointer items-center justify-center text-subtle-foreground"
                >
                  <ClearCircle className="size-4" />
                </button>
              )}

              {searchQuery && (
                <Button
                  type={ButtonType.Submit}
                  variant={ButtonVariant.Primary}
                  size={Size.Small}
                  radius={BorderRadius.Pill}
                  className="shrink-0 animate-fade-in"
                >
                  {t('mail.search')}
                </Button>
              )}
            </div>
          </form>
        </div>
      )}

      {!isSelecting && count > 0 && (
        <div className="flex items-center justify-between border-b border-divider pr-2 pl-5 pb-3">
          <p className="text-subhead-14 text-subtle-foreground">{title}</p>
          <MailActionsMenu
            canSelect={canSelect}
            canMarkAllAsRead={canMarkAllAsRead}
            onMark={onMark}
            onMarkAllRead={onMarkAllRead}
          />
        </div>
      )}

      {isSelecting && (
        <div className="flex items-center border-b border-divider pr-2 pl-5 pb-3">
          <div
            onClick={onToggleAll}
            className="flex cursor-pointer items-center gap-3"
          >
            <Checkbox checked={areAllSelected} onChange={() => {}} />
            <span className="text-body-14 text-muted-foreground">
              {selectedCount}
            </span>
            <p className="text-subhead-14 text-subtle-foreground">{title}</p>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {canMarkAsRead && (
              <button
                type="button"
                disabled={!hasSelection}
                onClick={onMarkSelectedRead}
                className={actionButton({ disabled: !hasSelection })}
              >
                <MailRead className="size-4.5" />
                {t('mail.markAsRead')}
              </button>
            )}
            {canRestore && (
              <button
                type="button"
                disabled={!hasSelection}
                onClick={onRestore}
                className={actionButton({ disabled: !hasSelection })}
              >
                <Retry className="size-[17px]" />
                {t('mail.restore')}
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                disabled={!hasSelection}
                onClick={onDelete}
                className={actionButton({ disabled: !hasSelection })}
              >
                <Trash className="size-4.5" />
                {t('mail.delete')}
              </button>
            )}
            {canDeletePermanently && (
              <button
                type="button"
                disabled={!hasSelection}
                onClick={onDeletePermanently}
                className={actionButton({ disabled: !hasSelection })}
              >
                <Trash className="size-4.5" />
                {t('mail.deletePermanently')}
              </button>
            )}
            <button
              type="button"
              aria-label={t('global.close')}
              onClick={onCancel}
              className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-3xl bg-secondary transition-colors hover:bg-secondary-hover"
            >
              <Close className="size-5.5 text-primary" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
